import React, { useState, useEffect } from 'react';
// import TranslationSimulator from './TranslationSimulator';  // Removed TranslationSimulator import
import './App.css';

function App() {
  const [apkInfo, setApkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  // Function to check if image exists by attempting to preload it
  const checkImageExists = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  };

  // Dynamically discover screenshots by checking for existence
  const [screenshots, setScreenshots] = useState([]);

  useEffect(() => {
    const loadScreenshots = async () => {
      const maxPossibleScreenshots = 20; // Reasonable upper limit
      const existingScreenshots = [];

      for (let i = 1; i <= maxPossibleScreenshots; i++) {
        const src = `/screenshots/${i}.png`;
        const exists = await checkImageExists(src);
        
        if (exists) {
          existingScreenshots.push({
            src: src,
            alt: `深语翻译应用界面截图 ${i}`
          });
        } else {
          // If we encounter a missing screenshot, stop checking further
          // This assumes screenshots are sequential (no gaps like 1.png, 2.png, 4.png)
          if (existingScreenshots.length > 0) {
            // If we already found some screenshots and now hit a missing one, 
            // we assume the sequence ended
            break;
          }
        }
      }
      
      setScreenshots(existingScreenshots);
    };

    loadScreenshots();
  }, []);

  useEffect(() => {
    // Fetch the APK information from the JSON file
    fetch('/downloads.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch APK information');
        }
        return response.json();
      })
      .then(data => {
        setApkInfo(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Function to get the latest APK for a specific architecture
  const getLatestApk = (arch) => {
    if (!apkInfo || !apkInfo.apks) return null;
    return apkInfo.apks.find(apk => 
      apk.version === apkInfo.latestVersion && apk.arch === arch
    );
  };

  // Get the latest APKs for both architectures
  const latestArm64 = getLatestApk('arm64-v8a');
  const latestArmv7 = getLatestApk('armeabi-v7a');

  // Function to close menu when clicking a link
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Navigation functions for screenshots
  const nextScreenshot = () => {
    if (screenshots.length > 0) {
      setCurrentScreenshot((prev) => (prev === screenshots.length - 1 ? 0 : prev + 1));
    }
  };

  const prevScreenshot = () => {
    if (screenshots.length > 0) {
      setCurrentScreenshot((prev) => (prev === 0 ? screenshots.length - 1 : prev - 1));
    }
  };

  return (
    <div className="App">
      {/* Header Section */}
      <header className="hero-section">
        <div className="container">
          <div className="header-nav">
            <div className="logo">
              <div className="logo-content">
                <img src="/logo192.png" alt="深语翻译 Logo" className="app-logo" />
                <div className="logo-text">
                  <h1 className="main-title">深语翻译</h1>
                  <h2 className="main-subtitle">Synodora Translate</h2>
                </div>
              </div>
            </div>
            {/* Mobile menu button - show on mobile screens */}
            <div className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <div className={`hamburger ${isMenuOpen ? 'active' : ''}`}></div>
            </div>
            {/* Desktop Navigation - show on larger screens */}
            <nav className="main-nav desktop-nav">
              <a href="#screenshots" onClick={handleLinkClick}>界面预览</a>
              <a href="#features" onClick={handleLinkClick}>功能特色</a>
              <a href="#solutions" onClick={handleLinkClick}>解决方案</a>
              <a href="#use-cases" onClick={handleLinkClick}>应用场景</a>
              <a href="#tech-advantages" onClick={handleLinkClick}>技术优势</a>
              <a href="#value-promise" onClick={handleLinkClick}>价值承诺</a>
            </nav>
          </div>
          {/* Mobile Navigation - show when menu is open */}
          <nav className={`main-nav mobile-nav ${isMenuOpen ? 'active' : ''}`}>
            <a href="#screenshots" onClick={handleLinkClick}>界面预览</a>
            <a href="#features" onClick={handleLinkClick}>功能特色</a>
            <a href="#solutions" onClick={handleLinkClick}>解决方案</a>
            <a href="#use-cases" onClick={handleLinkClick}>应用场景</a>
            <a href="#tech-advantages" onClick={handleLinkClick}>技术优势</a>
            <a href="#value-promise" onClick={handleLinkClick}>价值承诺</a>
          </nav>
          <p className="subtitle">以专业翻译品质，定义沟通新境界</p>
          <p className="tagline"><span className="highlight-text">环球交流，深译相通</span></p>
          
          {loading ? (
            <p className="loading-text">正在加载下载信息...</p>
          ) : error ? (
            <p className="error-text">加载失败: {error}</p>
          ) : (
            <div className="download-section">
              <div className="download-buttons">
                {latestArm64 && (
                  <a
                    className="download-button"
                    href={`/download/${latestArm64.fileName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    下载APK (64位) v{latestArm64.version}
                  </a>
                )}
                
                {latestArmv7 && (
                  <a
                    className="download-button secondary"
                    href={`/download/${latestArmv7.fileName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    下载APK (32位) v{latestArmv7.version}
                  </a>
                )}
              </div>
              
              {apkInfo && (
                <>
                  <p className="daily-limit">
                    当前版本: v{apkInfo.latestVersion} | 
                    发布日期: {apkInfo.releaseDate}
                  </p>
                  <p className="daily-limit">
                    每天免费畅享10000个字符的专业翻译服务！
                  </p>
                </>
              )}
            </div>
          )}
          
          <p className="intro-text">
            无论是出国旅行、国际会议、商务洽谈还是学术研究，深语翻译都能为您提供实时、精准、流畅的多语言解决方案，让"环球交流，深译相通"成为现实！
          </p>
        </div>
      </header>

      {/* Screenshots Section */}
      <section id="screenshots" className="screenshots-section">
        <div className="container">
          <h2 className="section-title">应用界面预览</h2>
          <h3 className="section-subtitle">App Interface Preview</h3>
          
          {screenshots.length > 0 ? (
            <div className="screenshots-container">
              <button className="screenshot-nav prev" onClick={prevScreenshot}>
                &#8249;
              </button>
              
              <div className="screenshot-item">
                <div className="screenshot-container">
                  <img 
                    src={screenshots[currentScreenshot]?.src} 
                    alt={screenshots[currentScreenshot]?.alt} 
                    className="screenshot-image" 
                  />
                </div>
              </div>
              
              <button className="screenshot-nav next" onClick={nextScreenshot}>
                &#8250;
              </button>
            </div>
          ) : (
            <p>暂无截图可用</p>
          )}
          
          {screenshots.length > 0 && (
            <div className="screenshot-indicators">
              {screenshots.map((_, index) => (
                <span 
                  key={index} 
                  className={`indicator ${index === currentScreenshot ? 'active' : ''}`}
                  onClick={() => setCurrentScreenshot(index)}
                ></span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">为什么选择深语翻译？</h2>
          <h3 className="section-subtitle">Why Synodora Translate?</h3>
          <div className="features-grid">
            <div className="feature-card">
              <h3>200+种语言全覆盖</h3>
              <p>全球主流语言、稀缺语言一网打尽，沟通无国界！</p>
            </div>
            <div className="feature-card">
              <h3>实时同声传译</h3>
              <p>会议、演讲、对话同步翻译，多语言混合输入无障碍，精度高、延时低，沟通如行云流水！</p>
            </div>
            <div className="feature-card">
              <h3>智能图片OCR翻译</h3>
              <p>对准菜单、路牌、说明书、海报一拍即译，文字识别精准，版式还原清晰，出行工作学习必备。</p>
            </div>
            <div className="feature-card">
              <h3>真人级语音朗读</h3>
              <p>语音识别精准，音色自然悦耳；支持面对面聊天翻译，对话流畅，如临其境。</p>
            </div>
            <div className="feature-card">
              <h3>翻译风格与术语控制</h3>
              <p>支持多领域翻译风格切换（法律、金融、医疗、工程等），可自定义术语库，确保专业表达准确一致，翻译结果更贴合场景。</p>
            </div>
            <div className="feature-card">
              <h3>上下文语境理解</h3>
              <p>不满足于逐句翻译，更能结合语境输出地道译文，行文自然流畅。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="solutions-section">
        <div className="container">
          <h2 className="section-title">全场景解决方案｜沟通无界</h2>
          <h3 className="section-subtitle">Full-Scenario Solutions | Seamless Communication</h3>
          <p className="section-description">从咖啡馆到国际会议厅，深语翻译让每次「环球交流」都「深译相通」：</p>
          
          <div className="solutions-grid">
            <div className="solution-card">
              <h3>专业翻译</h3>
              <p>每日10000字符免费额度，语境理解精准，风格灵活可调。</p>
            </div>
            <div className="solution-card">
              <h3>语音翻译｜对话翻译</h3>
              <p>面对面聊天即说即译，语音识别清晰，语音朗读自然。</p>
            </div>
            <div className="solution-card">
              <h3>同声传译</h3>
              <p>实时流式同传，支持100+种语言互译，支持多语种混合输入、双语输出、语音输出，稳定流畅。</p>
            </div>
            <div className="solution-card">
              <h3>图像翻译｜拍照翻译｜OCR翻译</h3>
              <p>一拍即译，文字版式双还原。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="use-cases-section">
        <div className="container">
          <h2 className="section-title">满足您全场景翻译需求</h2>
          <h3 className="section-subtitle">Meeting Your Full-Scenario Translation Needs</h3>
          
          <div className="use-cases-grid">
            <div className="use-case">
              <h3>出国旅游</h3>
              <p>菜单路牌即拍即译，问路购物轻松搞定。</p>
            </div>
            <div className="use-case">
              <h3>商务会议</h3>
              <p>选择领域、切换风格、搭配术语，实时同传打破语言障碍，谈判签约精准无误，以专业翻译驾驭全场。</p>
            </div>
            <div className="use-case">
              <h3>学术科研</h3>
              <p>高效翻译外文文献，专业术语精准传达，论文写作好帮手。</p>
            </div>
            <div className="use-case">
              <h3>语言学习</h3>
              <p>双语对照学习，自然语音辅助练习，沉浸式提升。</p>
            </div>
            <div className="use-case">
              <h3>日常交流</h3>
              <p>与外国朋友聊天、浏览外网资讯，快速翻译不求人。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Advantages Section */}
      <section id="tech-advantages" className="tech-advantages-section">
        <div className="container">
          <h2 className="section-title">技术优势｜信任之选</h2>
          <h3 className="section-subtitle">Technical Advantages | Choice You Can Trust</h3>
          
          <div className="tech-advantages-grid">
            <div className="tech-advantage-card">
              <h3>全球语言网络</h3>
              <p>覆盖200+种语言互译，专业术语库深度定制。</p>
            </div>
            <div className="tech-advantage-card">
              <h3>自然语音技术</h3>
              <p>基于先进语音合成与识别，输出清晰、自然的语音体验。</p>
            </div>
            <div className="tech-advantage-card">
              <h3>精准翻译系统</h3>
              <p>翻译风格、术语控制、领域适配，层层把关翻译质量。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Promise Section */}
      <section id="value-promise" className="value-promise-section">
        <div className="container">
          <h2 className="section-title">价值承诺</h2>
          <h3 className="section-subtitle">Value Promise</h3>
          
          <div className="value-promise-content">
            <p>当学术期刊要求术语零误差，当跨境并购需同步传递法律意图，深语翻译始终以专业能力践行：</p>
            <p className="promise-highlight">环球交流，深译相通。</p>
            <p>深语翻译 — 您值得托付的深度语言解决方案。</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-tagline"><span className="highlight-text">环球交流，深译相通</span></p>
          <p className="footer-name">深语翻译 — 您值得托付的深度语言解决方案</p>
          <p className="footer-subname">Synodora Translate - Your Trusted Deep Language Solution</p>
          <p className="registration-info">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
              闽ICP备2024068749号-3
            </a>
            <br />
            <span className="public-security-registration">
              <img src="/beian.png" alt="Public Security Registration" className="beian-icon" />
              <a href="https://beian.mps.gov.cn/#/query/webSearch?code=35050302001208" rel="noreferrer" target="_blank">闽公网安备35050302001208号</a>
            </span>
          </p>
          <p className="copyright">©2024-{new Date().getFullYear()} 泉州深语科技有限公司 版权所有</p>
        </div>
      </footer>
    </div>
  );
}

export default App;